/**
  * 基础信息组件
  * 
  * 版本: 1.1.1
  * 变更记录:
  * - 2025-09-29: 1.1.1 物业费输入框样式与其他输入保持一致（加入 input-yellow）
  * - 2025-09-29: 1.1.0 新增“物业费（元/月）”可选输入，并在计算中与租金相加
  */
import React, { useCallback } from 'react';
import { OperatingCostRatios } from '../utils/dataModel';

interface BasicInfoProps {
  filteredData: {
    provinces: string[];
    cities: Record<string, string[]>;
    districts: Record<string, string[]>;
    cityTiers: Record<string, string>;
    districtTiers: Record<string, string>;
    businessAreas: string[];
    operatingCostRatios: Record<string, OperatingCostRatios>;
  };
  basicInfo: {
    province: string;
    city: string;
    district: string;
    cityTier: string;
    businessArea: string;
    area: number;
    estimatedRevenue: number;
    fixedRent: number;
    rentPercentage: number;
    propertyFee?: number;
  };
  setBasicInfo: React.Dispatch<React.SetStateAction<{
    province: string;
    city: string;
    district: string;
    cityTier: string;
    businessArea: string;
    area: number;
    estimatedRevenue: number;
    fixedRent: number;
    rentPercentage: number;
    propertyFee?: number;
  }>>;
  hasAttemptedSubmit?: boolean;
}

// 性能优化：使用React.memo包装组件，避免不必要的重渲染
const BasicInfo: React.FC<BasicInfoProps> = React.memo(({ filteredData, basicInfo, setBasicInfo, hasAttemptedSubmit = false }) => {
  const handleProvinceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const province = e.target.value;
    setBasicInfo(prev => ({
      ...prev,
      province,
      city: '',
      district: '',
      cityTier: ''
    }));
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const city = e.target.value;
    setBasicInfo(prev => ({
      ...prev,
      city,
      district: '',
      cityTier: ''
    }));
  };

  const handleDistrictChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const district = e.target.value;
    const fullDistrictKey = `${basicInfo.province}-${basicInfo.city}-${district}`;
    setBasicInfo(prev => ({
      ...prev,
      district,
      cityTier: filteredData.districtTiers[fullDistrictKey] || ''
    }));
  }, [basicInfo.province, basicInfo.city, filteredData.districtTiers, setBasicInfo]);

  const handleBusinessAreaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setBasicInfo(prev => ({
      ...prev,
      businessArea: e.target.value
    }));
  };

  const handleAreaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBasicInfo(prev => ({
      ...prev,
      area: parseFloat(e.target.value) || 0
    }));
  };

  const handleEstimatedRevenueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBasicInfo(prev => ({
      ...prev,
      estimatedRevenue: parseFloat(e.target.value) || 0
    }));
  };

  const handleFixedRentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBasicInfo(prev => ({
      ...prev,
      fixedRent: parseFloat(e.target.value) || 0
    }));
  };

  const handleRentPercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBasicInfo(prev => ({
      ...prev,
      rentPercentage: parseFloat(e.target.value) || 0
    }));
  };

  const handlePropertyFeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const propertyFee = parseFloat(e.target.value);
    setBasicInfo(prev => ({
      ...prev,
      propertyFee: isNaN(propertyFee) ? 0 : Math.max(0, propertyFee)
    }));
  };

  return (
    <div className="mb-2">
      <h2 className="section-title title-basic-info mb-0 w-full cursor-pointer">基础信息
        {hasAttemptedSubmit && <span className="text-xs text-yellow-600 ml-1">（*黄色区域必填）</span>}
      </h2>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3 mt-1">
        <div className="form-group mb-1">
          <label htmlFor="province" className="form-label mb-0 text-xs">省：{hasAttemptedSubmit && !basicInfo.province ? <span className="text-red-500">*</span> : null}</label>
          <select 
            id="province" 
            className={`form-select py-1 px-2 text-sm input-yellow ${hasAttemptedSubmit && !basicInfo.province ? 'border-red-500' : ''}`}
            value={basicInfo.province}
            onChange={handleProvinceChange}
          >
            <option value="">请选择省份</option>
            {filteredData.provinces.map(province => (
              <option key={province} value={province}>{province}</option>
            ))}
          </select>
        </div>
        
        <div className="form-group mb-1">
          <label htmlFor="city" className="form-label mb-0 text-xs">市：{hasAttemptedSubmit && !basicInfo.city ? <span className="text-red-500">*</span> : null}</label>
          <select 
            id="city" 
            className={`form-select py-1 px-2 text-sm input-yellow ${hasAttemptedSubmit && !basicInfo.city ? 'border-red-500' : ''}`}
            value={basicInfo.city}
            onChange={handleCityChange}
            disabled={!basicInfo.province}
          >
            <option value="">请选择城市</option>
            {basicInfo.province && filteredData.cities[basicInfo.province]?.map(city => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>
        
        <div className="form-group mb-1">
          <label htmlFor="district" className="form-label mb-0 text-xs">区县：{hasAttemptedSubmit && !basicInfo.district ? <span className="text-red-500">*</span> : null}</label>
          <select 
            id="district" 
            className={`form-select py-1 px-2 text-sm input-yellow ${hasAttemptedSubmit && !basicInfo.district ? 'border-red-500' : ''}`}
            value={basicInfo.district}
            onChange={handleDistrictChange}
            disabled={!basicInfo.city}
          >
            <option value="">请选择区县</option>
            {basicInfo.city && filteredData.districts[basicInfo.city]?.map(district => (
              <option key={district} value={district}>{district}</option>
            ))}
          </select>
        </div>
        
        <div className="form-group mb-1">
          <label htmlFor="businessArea" className="form-label mb-0 text-xs">商圈类型：{hasAttemptedSubmit && !basicInfo.businessArea ? <span className="text-red-500">*</span> : null}</label>
          <select 
            id="businessArea" 
            className={`form-select py-1 px-2 text-sm input-yellow ${hasAttemptedSubmit && !basicInfo.businessArea ? 'border-red-500' : ''}`}
            value={basicInfo.businessArea}
            onChange={handleBusinessAreaChange}
          >
            <option value="">请选择商圈类型</option>
            {filteredData.businessAreas.map(area => (
              <option key={area} value={area}>{area}</option>
            ))}
          </select>
        </div>

        <div className="form-group mb-1">
          <label htmlFor="area" className="form-label mb-0 text-xs">面积（㎡）：{hasAttemptedSubmit && !basicInfo.area ? <span className="text-red-500">*</span> : null}</label>
          <input 
            type="number" 
            id="area" 
            className={`form-input py-1 px-2 text-sm input-yellow ${hasAttemptedSubmit && !basicInfo.area ? 'border-red-500' : ''}`}
            value={basicInfo.area || ''}
            onChange={handleAreaChange}
            min="0"
            step="1"
          />
        </div>
        
        <div className="form-group mb-1">
          <label htmlFor="estimatedRevenue" className="form-label mb-0 text-xs">预估月营业额（元）：{hasAttemptedSubmit && !basicInfo.estimatedRevenue ? <span className="text-red-500">*</span> : null}</label>
          <input 
            type="number" 
            id="estimatedRevenue" 
            className={`form-input py-1 px-2 text-sm input-yellow ${hasAttemptedSubmit && !basicInfo.estimatedRevenue ? 'border-red-500' : ''}`}
            value={basicInfo.estimatedRevenue || ''}
            onChange={handleEstimatedRevenueChange}
            min="0"
            step="1000"
          />
        </div>
        
        <div className="form-group mb-1">
          <label htmlFor="fixedRent" className="form-label mb-0 text-xs">固定租金（元/月）：{hasAttemptedSubmit && !basicInfo.fixedRent && !basicInfo.rentPercentage ? <span className="text-orange-500">†</span> : null}</label>
          <input 
            type="number" 
            id="fixedRent" 
            className={`form-input py-1 px-2 text-sm input-yellow ${hasAttemptedSubmit && !basicInfo.fixedRent && !basicInfo.rentPercentage ? 'border-red-500' : ''}`}
            value={basicInfo.fixedRent || ''}
            onChange={handleFixedRentChange}
            min="0"
            step="100"
          />
          <div className="text-xs text-gray-400 mt-1">与租金抽成二选一，可同时填写</div>
        </div>

        <div className="form-group mb-1">
          <label htmlFor="propertyFee" className="form-label mb-0 text-xs">物业费（元/月）：</label>
          <input 
            type="number" 
            id="propertyFee" 
            className="form-input py-1 px-2 text-sm input-yellow"
            value={(basicInfo.propertyFee ?? 0) || ''}
            onChange={handlePropertyFeeChange}
            min="0"
            step="50"
          />
          <div className="text-xs text-gray-400 mt-1">可选输入，参与租金合计，不影响租金抽成计算</div>
        </div>
        
        <div className="form-group mb-1">
          <label htmlFor="rentPercentage" className="form-label mb-0 text-xs">租金抽成（%）：{hasAttemptedSubmit && !basicInfo.rentPercentage && !basicInfo.fixedRent ? <span className="text-orange-500">†</span> : null}</label>
          <input 
            type="number" 
            id="rentPercentage" 
            className={`form-input py-1 px-2 text-sm input-yellow ${hasAttemptedSubmit && !basicInfo.rentPercentage && !basicInfo.fixedRent ? 'border-red-500' : ''}`}
            value={basicInfo.rentPercentage || ''}
            onChange={handleRentPercentageChange}
            min="0"
            max="100"
            step="0.1"
          />
          <div className="text-xs text-gray-400 mt-1">与固定租金二选一，可同时填写</div>
        </div>
      </div>
    </div>
  );
}); // React.memo结束

export default BasicInfo;